"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
function _export(target, all) {
    for(var name in all)Object.defineProperty(target, name, {
        enumerable: true,
        get: Object.getOwnPropertyDescriptor(all, name).get
    });
}
_export(exports, {
    get BMI_UNDER_18_NOTE () {
        return BMI_UNDER_18_NOTE;
    },
    get VITALS_RANGES () {
        return VITALS_RANGES;
    },
    get VitalsInputSchema () {
        return VitalsInputSchema;
    },
    get VitalsSaveRequestSchema () {
        return VitalsSaveRequestSchema;
    },
    get VitalsSaveResponseSchema () {
        return VitalsSaveResponseSchema;
    },
    get bmiCategory () {
        return bmiCategory;
    },
    get computeBmi () {
        return computeBmi;
    },
    get parseBloodPressure () {
        return parseBloodPressure;
    }
});
const _zod = require("zod");
const VITALS_RANGES = {
    bpSystolic: {
        min: 50,
        max: 300
    },
    bpDiastolic: {
        min: 20,
        max: 200
    },
    heartRate: {
        min: 20,
        max: 250
    },
    respiratoryRate: {
        min: 4,
        max: 80
    },
    temperatureC: {
        min: 30,
        max: 45
    },
    weightKg: {
        min: 0.5,
        max: 400
    },
    heightCm: {
        min: 30,
        max: 250
    }
};
/**
 * One optional measurement. `null` and absent both mean "not taken". Messages
 * are fixed strings so a rejected value never ends up in a log or response.
 */ const measurement = (key, label, isInteger)=>{
    const { min, max } = VITALS_RANGES[key];
    const rangeMessage = `${label} must be between ${min} and ${max}.`;
    let schema = _zod.z.number({
        message: `${label} must be a number.`
    });
    if (isInteger) {
        schema = schema.int({
            message: `${label} must be a whole number.`
        });
    }
    return schema.min(min, {
        message: rangeMessage
    }).max(max, {
        message: rangeMessage
    }).nullable().optional();
};
const vitalsShape = {
    bpSystolic: measurement('bpSystolic', 'Systolic blood pressure', true),
    bpDiastolic: measurement('bpDiastolic', 'Diastolic blood pressure', true),
    heartRate: measurement('heartRate', 'Heart rate', true),
    respiratoryRate: measurement('respiratoryRate', 'Respiratory rate', true),
    temperatureC: measurement('temperatureC', 'Temperature', false),
    weightKg: measurement('weightKg', 'Weight', false),
    heightCm: measurement('heightCm', 'Height', false)
};
/**
 * A blood pressure is only meaningful as a pair, and systolic must exceed
 * diastolic (a reversed pair is almost always a transposition).
 */ const checkBloodPressure = (vitals, ctx)=>{
    const hasSystolic = vitals.bpSystolic !== null && vitals.bpSystolic !== undefined;
    const hasDiastolic = vitals.bpDiastolic !== null && vitals.bpDiastolic !== undefined;
    if (hasSystolic !== hasDiastolic) {
        ctx.addIssue({
            code: 'custom',
            path: [
                hasSystolic ? 'bpDiastolic' : 'bpSystolic'
            ],
            message: 'Enter both systolic and diastolic blood pressure.'
        });
        return;
    }
    if (hasSystolic && hasDiastolic && vitals.bpSystolic <= vitals.bpDiastolic) {
        ctx.addIssue({
            code: 'custom',
            path: [
                'bpSystolic'
            ],
            message: 'Systolic must be higher than diastolic.'
        });
    }
};
const VitalsInputSchema = _zod.z.object(vitalsShape).strict().superRefine(checkBloodPressure);
const VitalsSaveRequestSchema = _zod.z.object({
    ...vitalsShape,
    version: _zod.z.number({
        message: 'version must be a number.'
    }).int({
        message: 'version must be a whole number.'
    }).min(0, {
        message: 'version must not be negative.'
    })
}).strict().superRefine(checkBloodPressure);
const VitalsSaveResponseSchema = _zod.z.object({
    id: _zod.z.string().uuid(),
    version: _zod.z.number().int().min(1),
    bmi: _zod.z.number().nullable(),
    updatedAt: _zod.z.string().datetime({
        offset: true
    })
}).strict();
const BLOOD_PRESSURE_TEXT = /^\s*(\d{2,3})\s*\/\s*(\d{2,3})\s*$/;
const parseBloodPressure = (text)=>{
    const match = BLOOD_PRESSURE_TEXT.exec(text);
    if (match === null) {
        return null;
    }
    const systolic = Number(match[1]);
    const diastolic = Number(match[2]);
    if (systolic <= diastolic) {
        return null;
    }
    return {
        systolic,
        diastolic
    };
};
const computeBmi = (weightKg, heightCm)=>{
    if (weightKg === null || weightKg === undefined || heightCm === null || heightCm === undefined) {
        return null;
    }
    if (weightKg <= 0 || heightCm <= 0) {
        return null;
    }
    const heightM = heightCm / 100;
    const bmi = weightKg / (heightM * heightM);
    return Math.round(bmi * 10) / 10;
};
const BMI_UNDER_18_NOTE = 'Category not shown for under-18';
const ADULT_AGE_YEARS = 18;
const bmiCategory = (bmi, ageYears)=>{
    if (ageYears < ADULT_AGE_YEARS) {
        return null;
    }
    if (bmi < 18.5) {
        return {
            key: 'underweight',
            label: 'Underweight'
        };
    }
    if (bmi < 23) {
        return {
            key: 'normal',
            label: 'Normal'
        };
    }
    if (bmi < 25) {
        return {
            key: 'at_risk',
            label: 'At risk'
        };
    }
    if (bmi < 30) {
        return {
            key: 'overweight',
            label: 'Overweight'
        };
    }
    return {
        key: 'obese',
        label: 'Obese'
    };
};
